<template>
  <DefaultModalsContainer
    v-if="isOpen"
  >
    <template
      v-slot:heading
    >
      <div
        class="modal__head"
      >
        <h2 v-if="isFormState" class="text-lg md:text-xl font-semibold text-gray-900">
          {{ $t('modal.psc.heading') }}
        </h2>
        <h2 v-if="isRegisteredState" class="text-lg md:text-xl font-semibold text-gray-900">
          {{ $t('modal.psc.heading-complete') }}
        </h2>
        <h2 v-if="isSavedState" class="text-lg md:text-xl font-semibold text-gray-900">
          {{ $t('modal.psc.heading-saved') }}
        </h2>
        <button
          @click="closeModal"
          class="p-1 hover:bg-gray-100 rounded-full transition-colors"
          type="button"
        >
          <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    </template>
    <template
      v-slot:content
    >
      <div class="modal__body scrollbar-hide h-full">
        <!-- Form state -->
        <div v-if="isFormState" class="modal__copy">
          <p class="text-sm" v-html="$t('modal.psc.copy')" />
          <div class="text-xs pt-2" v-html="$t('modal.psc.copy-disclaimer')" />
        </div>
        <!-- Registered state -->
        <div v-if="isRegisteredState" class="modal__copy">
          <p class="text-sm" v-html="$t('modal.psc.copy-complete')" />
        </div>
        <!-- Saved state -->
        <div v-if="isSavedState" class="modal__copy">
          <p class="text-sm" v-html="$t('modal.psc.copy-saved')" />
        </div>
        <!-- Form -->
        <UForm v-if="isFormState" :validate="validate" :validate-on="['submit', 'change']" :state="state" class="modal__form" @submit="onSubmit" ref="form">
          <div>
            <!-- Text Input -->
            <div class="field">
              <label for="modal.psc" class="block mb-1">
                {{ $t('modal.psc.input-label') }}<span>*</span>
              </label>
              <input
                v-model="state.psc"
                v-maska="'### ##'"
                id="modal.psc"
                name="psc"
                type="text"
                maxlength="6"
                @change="updateValidation('psc')"
                :placeholder="$t('modal.psc.input-placeholder')"
              />

              <p v-for="error in getErrors('psc')" class="text-xs leading-tight mt-1.5 text-primary-500">
                {{ error.message}}
              </p>
            </div>

            <!-- Checkbox with Disclaimer -->
            <div class="mb-6">
              <div class="field field--checkbox">
                <label for="ageConsent" class="">
                  <input
                    id="ageConsent"
                    v-model="state.ageConsent"
                    type="checkbox"
                    @change="updateValidation('ageConsent')"
                  />
                  <div>
                    <span>{{ $t('modal.psc.checkbox-label-prefix') }}</span>
                    <a
                      href="https://www.cncenter.cz/pravidla-hry-bleskove-hry"
                      target="_blank"
                      rel="noopener"
                      @click.stop
                    >
                      {{ $t('modal.psc.checkbox-label-link') }}
                    </a>
                    <span>{{ $t('modal.psc.checkbox-label-suffix') }}</span>
                    <a
                      href="https://www.cncenter.cz/informace-o-zpracovani-akce"
                      target="_blank"
                      rel="noopener"
                      @click.stop
                    >
                      {{ $t('modal.psc.checkbox-label-link-privacy') }}
                    </a>
                    <span>*</span>
                  </div>
                </label>

                <p v-for="error in getErrors('ageConsent')" class="text-xs leading-tight mt-1.5 text-primary-500">
                  {{ error.message}}
                </p>
              </div>
            </div>
          </div>

          <div v-if="errorMessage && !errorMessageHide" class="text-primary-500">
            {{errorMessage}}
          </div>

          <div class="modal__action">
            <button
              @click="closeModal"
              type="button"
              class="btn btn--border"
              :disabled="isLoading"
              :loading="isLoading"
            >
              {{ $t('modal.psc.btn-cancel') }}
            </button>

            <button
              type="submit"
              class="btn btn--primary"
              :disabled="isLoading || !isFormValid"
              :loading="isLoading"
            >
              {{ $t('modal.psc.btn-submit') }}
            </button>
          </div>

          <!-- Footer Buttons -->
        </UForm>

        <div v-if="isRegisteredState || isSavedState" class="modal__action">
          <button
            @click="closeModal"
            type="button"
            class="btn btn--primary"
            :disabled="isLoading"
            :loading="isLoading"
          >
            {{ $t('modal.psc.btn-complete') }}
          </button>
        </div>
      </div>
    </template>
  </DefaultModalsContainer>
</template>

<script setup lang="ts">
import type { FormError, FormSubmitEvent } from '#ui/types'
import {reactive, defineProps, defineEmits, type PropType, ref} from 'vue'
import { vMaska } from "maska/vue"
import { useI18n } from "vue-i18n"
import {PscModalStep} from "~/libs/constants/PscModalStep";
import {pushToDataLayer} from "~/composables/useAnalytics";
const {t} = useI18n()

const props = defineProps({
  isOpen: { type: Boolean, required: false, default: false },
  isLoading: { type: Boolean, required: false, default: false },
  step: {type: String as PropType<PscModalStep>, required: false, default: PscModalStep.FORM},
  psc: {type: String, required: false, default: null},
  ageConsent: {type: Boolean, required: false, default: false},
  errorMessage: { type: String, required: false, default: undefined },
})

const emits = defineEmits(['close', 'submit'])

const state = reactive({
  psc: props.psc,
  ageConsent: props.ageConsent,
})

const changedValues = reactive({
  psc: false,
  ageConsent: false,
})

const valueChanged = (key: string) => {
  if (changedValues.hasOwnProperty(key)) {
    // @ts-ignore
    changedValues[key] = true
  }
}

const errorMessageHide = ref(false)
const validateAllFields = ref(false)

const form = ref()

const getErrors = (path?: string) => {
  if (!path) {
    return form.value?.getErrors()
  }
  return form.value?.getErrors().filter((error: any) => {
    return error.path === path
  })
}

const isFormState = computed(() => {
  return (props.step === PscModalStep.FORM)
})
const isRegisteredState = computed(() => {
  return (props.step === PscModalStep.REGISTERED)
})
const isSavedState = computed(() => {
  return (props.step === PscModalStep.SAVED)
})

const closeModal = (): void => {
  emits('close')
}
const updateValidation = async (key: string | undefined, allFields: boolean = false) => {
  if (key) {
    valueChanged(key)
  }
  try {
    validateAllFields.value = allFields
    await form.value?.validate()
    validateAllFields.value = false
  } catch (e) {}
}

const validate = (state: any): FormError[] => {
  errorMessageHide.value = true
  const errors = []

  // psc
  if (validateAllFields.value || changedValues.psc) {
    if (!state.psc) {
      errors.push({path: 'psc', message: t('modal.psc.error-psc-required')})
    }
    const unmaskedPsc = unmaskPsc(state.psc)
    if (!unmaskedPsc || String(unmaskedPsc).length !== 5) {
      errors.push({path: 'psc', message: t('modal.psc.error-psc-number')})
    }
  }

  // age consent
  if ((validateAllFields.value || changedValues.ageConsent) && !state.ageConsent) {
    errors.push({ path: 'ageConsent', message: t('modal.psc.error-age-consent') })
  }

  return errors
}

const isFormValid = computed(() => {
  return state.ageConsent && state.psc && form.value?.getErrors()?.length === 0
})

const unmaskPsc = (psc?: string): Number => {
  return Number(psc?.replace(/\s/g, ''))
}

const onSubmit = async (event: FormSubmitEvent<any>) => {
  if (props.isLoading) {
    return false
  }
  await updateValidation(undefined, true)
  if (!isFormValid.value) {
    return false
  }

  const payload = {
    psc: unmaskPsc(event.data.psc),
    ageConsent: event.data.ageConsent
  }

  // --- ANALYTIKA PRO SUBMIT START ---
  pushToDataLayer({
    "event": "clickTo_content",
    "clickTo": "pscZadano",
    "link": {
      "psc": String(payload.psc),
      "text": t('modal.psc.btn-submit')
    }
  });
  // ------------------------------------

  emits('submit', payload)
  errorMessageHide.value = false
}
</script>

<style scoped>
</style>
